import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Wrench } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { marginTone } from './MarginViewSwitcher';

const fmtA = (v) => `A$${Math.round(v || 0).toLocaleString()}`;
const fixLink = (key) => `/admin?tab=price-book&component=${encodeURIComponent(key)}`;

function BreakdownCard({ title, sub, rows, keyField, labelField, testId, minPct, flags }) {
  const maxSell = Math.max(...rows.map((r) => r.sell_aud || 0), 1);
  return (
    <Card className="border-slate-200" data-testid={testId}>
      <CardContent className="p-5">
        <h3 className="text-sm font-semibold text-[#0F2040] mb-0.5">{title}</h3>
        <p className="text-[11px] text-slate-400 mb-3">{sub}</p>
        {rows.length === 0 ? <p className="text-xs text-slate-400" data-testid={`${testId}-empty`}>No saved product subscriptions yet.</p> : (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
              <th className="py-1.5 pr-2">{title.replace('Product margin by ', '')}</th><th className="py-1.5 pr-2 w-[24%]">Share of product sell</th>
              <th className="py-1.5 pr-2 text-right">Deals</th><th className="py-1.5 pr-2 text-right">Sell (A$)</th><th className="py-1.5 pr-2 text-right">Cost (A$)</th>
              <th className="py-1.5 pr-2 text-right">Margin (A$)</th><th className="py-1.5 text-right">Margin %</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const rid = String(r[keyField]).toLowerCase().replace(/\s+/g, '-');
                const flagged = flags && (r.below_cost || r.below_guardrail);
                return (
                  <tr key={r[keyField]} className={`border-b border-slate-100 ${r.below_cost && flags ? 'bg-rose-50/70' : flagged ? 'bg-amber-50/60' : ''}`} data-testid={`${testId}-row-${rid}`}>
                    <td className="py-1.5 pr-2 font-semibold text-[#0F2040]">
                      {r[labelField]}
                      {flagged && (
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 font-normal" data-testid={`${testId}-flag-${rid}`}>
                          <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${r.below_cost ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-800'}`}>
                            <AlertTriangle className="w-3 h-3" /> {r.below_cost ? `Sold below cost${r.deals_below_cost ? ` · ${r.deals_below_cost} deal${r.deals_below_cost === 1 ? '' : 's'}` : ''}` : `Below ${minPct}% guardrail`}
                          </span>
                          <Link to={fixLink(r.key)} className="inline-flex items-center gap-0.5 text-[10px] text-[#0F2040] underline hover:text-[#4ABFCF]" data-testid={`${testId}-fix-${rid}`} title="Open this component's unit rates in the price book">
                            <Wrench className="w-3 h-3" /> Fix the rate <ArrowRight className="w-3 h-3" />
                          </Link>
                        </div>
                      )}
                    </td>
                    <td className="py-1.5 pr-2 align-top pt-2.5"><div className="h-1.5 rounded-full bg-slate-100"><div className={`h-1.5 rounded-full ${r.below_cost && flags ? 'bg-rose-500' : 'bg-violet-500'}`} style={{ width: `${Math.max(2, (r.sell_aud / maxSell) * 100)}%` }} /></div></td>
                    <td className="py-1.5 pr-2 text-right text-slate-500 align-top">{r.deals}</td>
                    <td className="py-1.5 pr-2 text-right align-top">{fmtA(r.sell_aud)}</td>
                    <td className="py-1.5 pr-2 text-right align-top">{r.cost_known === false ? <span className="text-slate-300" title="Cost basis incomplete for this component">~{fmtA(r.cost_aud)}</span> : fmtA(r.cost_aud)}</td>
                    <td className={`py-1.5 pr-2 text-right font-semibold align-top ${r.margin_aud < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{fmtA(r.margin_aud)}</td>
                    <td className={`py-1.5 text-right font-semibold align-top ${marginTone(r.margin_pct, minPct)}`}>{r.margin_pct != null ? `${r.margin_pct}%` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

/** Product view extras: subscription margin by package and by component (full term, AUD) with below-cost / below-guardrail flags. */
export function ProductMarginBreakdownCards({ byPackage, byComponent, minPct }) {
  const belowCost = (byComponent || []).filter((c) => c.below_cost);
  return (
    <div className="space-y-4" data-testid="product-margin-breakdowns">
      {belowCost.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="product-below-cost-banner">
          <AlertTriangle className="w-4 h-4" />
          <span><b>{belowCost.length} component{belowCost.length === 1 ? '' : 's'} sold below cost</b> across the portfolio: {belowCost.map((c) => c.label).join(', ')}. Review the unit rates or cost basis in the price book.</span>
          <Link to={fixLink(belowCost[0].key)} className="ml-auto inline-flex items-center gap-1 rounded-md bg-[#0F2040] px-2 py-1 text-[11px] font-medium text-white hover:bg-[#1a3260]" data-testid="product-below-cost-fix">
            <Wrench className="w-3 h-3" /> Open price book <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}
      <div className="grid xl:grid-cols-2 gap-4">
        <BreakdownCard title="Product margin by package" sub="Every saved subscription, summed over its term. Cost from the price-book cost basis." rows={byPackage || []} keyField="package" labelField="package" testId="product-margin-by-package" minPct={minPct} />
        <BreakdownCard title="Product margin by component" sub="Active users, platform, storage, analytics… across all subscriptions. Red = sold below cost; amber = below the margin guardrail." rows={byComponent || []} keyField="key" labelField="label" testId="product-margin-by-component" minPct={minPct} flags />
      </div>
    </div>
  );
}
